/**
 * CDP SDK Storage Utilities
 * Cookie and localStorage management
 */

import { log, warn, isLocalStorageAvailable } from './utils';

// =============================================================================
// PERSISTENCE GATE (СТ22, launch 07.10)
// =============================================================================

/**
 * Whether the SDK may write to the visitor's device (cookies, localStorage,
 * sessionStorage). ePrivacy art. 5(3): storing anything on the device for
 * measurement needs consent first. The init resolves the posture and opens the
 * gate when the site's mode is `implied` or the visitor has granted something;
 * until then every write below is a no-op, so the SDK works from memory (an
 * anonymous count per page, no identifier that outlives it).
 *
 * The visitor's decision itself is the one exception — remembering "no" is
 * strictly necessary, or the banner would ask on every page.
 *
 * Starts CLOSED: nothing is written before the posture is known. Reads are never
 * gated.
 */
let persistenceAllowed = false;

const DECISION_KEYS = /^cdp_consent/;

export function setPersistenceAllowed(allowed: boolean): void {
  persistenceAllowed = allowed;
}

export function isPersistenceAllowed(): boolean {
  return persistenceAllowed;
}

function mayWrite(key: string): boolean {
  return persistenceAllowed || DECISION_KEYS.test(key);
}

// =============================================================================
// COOKIE UTILITIES
// =============================================================================

/**
 * Get a cookie value by name
 */
export function getCookie(name: string): string | null {
  try {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  } catch (e) {
    warn('Failed to get cookie:', name, e);
    return null;
  }
}

/**
 * `; Secure` only when the page is actually https — a Secure cookie is silently
 * DISCARDED by the browser on http, which would wipe identity on plain-http
 * sites and on local dev. http://localhost is exempt in most browsers, but not
 * every http origin is, so gate on the protocol rather than assume.
 */
function secureAttr(): string {
  try {
    return window.location.protocol === 'https:' ? '; Secure' : '';
  } catch {
    return '';
  }
}

/**
 * Set a cookie with optional domain and expiration
 */
export function setCookie(
  name: string,
  value: string,
  days: number = 365,
  domain?: string
): void {
  if (!mayWrite(name)) return;
  try {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    const domainPart = domain ? `; domain=${domain}` : '';
    // A site without the loader learns its cookieDomain only from the profile, so the very first write was
    // host-only — and a host-only cookie is read before the domain one. Two copies made reset() useless:
    // it changed the domain copy and the next page read the old host-only one (24chasa, 26.10 —
    // NEWS-LOGIN-IDENTITY §11). Writing the domain copy retires the host-only one.
    if (domain) document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/${domainPart}; SameSite=Lax${secureAttr()}`;
    log('Cookie set:', name);
  } catch (e) {
    warn('Failed to set cookie:', name, e);
  }
}

/**
 * Delete a cookie
 */
export function deleteCookie(name: string, domain?: string): void {
  try {
    const domainPart = domain ? `; domain=${domain}` : '';
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainPart}; SameSite=Lax`;
    // …and the host-only copy a first write may have left (see setCookie).
    if (domain) document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
    log('Cookie deleted:', name);
  } catch (e) {
    warn('Failed to delete cookie:', name, e);
  }
}

// =============================================================================
// LOCAL STORAGE UTILITIES
// =============================================================================

/**
 * Get a value from localStorage (with JSON parsing)
 */
export function getStorage<T>(key: string): T | null {
  if (!isLocalStorageAvailable()) {
    return null;
  }

  try {
    const value = localStorage.getItem(key);
    if (value === null) {
      return null;
    }
    return JSON.parse(value) as T;
  } catch (e) {
    warn('Failed to get from localStorage:', key, e);
    return null;
  }
}

/**
 * Set a value in localStorage (with JSON stringification)
 */
export function setStorage<T>(key: string, value: T): void {
  if (!mayWrite(key)) return;
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    localStorage.setItem(key, JSON.stringify(value));
    log('Storage set:', key);
  } catch (e) {
    warn('Failed to set localStorage:', key, e);
  }
}

/**
 * Remove a value from localStorage
 */
export function removeStorage(key: string): void {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    localStorage.removeItem(key);
    log('Storage removed:', key);
  } catch (e) {
    warn('Failed to remove from localStorage:', key, e);
  }
}

/**
 * Get a string value from localStorage (no JSON parsing)
 */
export function getStorageString(key: string): string | null {
  if (!isLocalStorageAvailable()) {
    return null;
  }

  try {
    return localStorage.getItem(key);
  } catch (e) {
    warn('Failed to get string from localStorage:', key, e);
    return null;
  }
}

/**
 * Set a string value in localStorage (no JSON stringification)
 */
export function setStorageString(key: string, value: string): void {
  if (!mayWrite(key)) return;
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    localStorage.setItem(key, value);
  } catch (e) {
    warn('Failed to set string in localStorage:', key, e);
  }
}

// =============================================================================
// SESSION STORAGE UTILITIES
// =============================================================================

/**
 * Get a value from sessionStorage
 */
export function getSessionStorage<T>(key: string): T | null {
  try {
    const value = sessionStorage.getItem(key);
    if (value === null) {
      return null;
    }
    return JSON.parse(value) as T;
  } catch (e) {
    warn('Failed to get from sessionStorage:', key, e);
    return null;
  }
}

/**
 * Set a value in sessionStorage
 */
export function setSessionStorage<T>(key: string, value: T): void {
  if (!mayWrite(key)) return;
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    warn('Failed to set sessionStorage:', key, e);
  }
}

/**
 * Remove a value from sessionStorage
 */
export function removeSessionStorage(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch (e) {
    warn('Failed to remove from sessionStorage:', key, e);
  }
}

// =============================================================================
// HYBRID STORAGE (Cookie + LocalStorage fallback)
// =============================================================================

/**
 * Get a value using cookie first, then localStorage fallback
 */
export function getHybrid(key: string): string | null {
  // Try cookie first
  const cookieValue = getCookie(key);
  if (cookieValue !== null) {
    return cookieValue;
  }

  // Fall back to localStorage
  return getStorageString(key);
}

/**
 * Set a value in both cookie and localStorage for redundancy
 */
export function setHybrid(
  key: string,
  value: string,
  days: number = 365,
  domain?: string
): void {
  setCookie(key, value, days, domain);
  setStorageString(key, value);
}

/**
 * Delete a value from both cookie and localStorage
 */
export function deleteHybrid(key: string, domain?: string): void {
  deleteCookie(key, domain);
  removeStorage(key);
}
